import { z } from 'zod';

/** Provider-neutral advertising contracts shared by the API, worker and admin (MASTER_SPEC §14–15). Inputs are Zod; outputs are plain types. */

export const adProviderKeys = ['google-ads', 'linkedin-ads', 'dry-run'] as const;
export type AdProviderKey = (typeof adProviderKeys)[number];

export const adCampaignStates = ['DRAFT', 'IN_REVIEW', 'APPROVED', 'PUBLISHING', 'PUBLISHED', 'FAILED', 'OUTCOME_UNKNOWN', 'ARCHIVED'] as const;
export type AdCampaignState = (typeof adCampaignStates)[number];

export const adPublishOperationStates = ['INTENT', 'ATTEMPTING', 'ACCEPTED', 'REJECTED', 'UNKNOWN', 'RECONCILED_FOUND', 'RECONCILED_NOT_FOUND'] as const;
export type AdPublishOperationState = (typeof adPublishOperationStates)[number];

export const adConnectionStatuses = ['UNCONFIGURED', 'DISCONNECTED', 'CONNECTED', 'EXPIRED', 'ERROR'] as const;
export type AdConnectionStatus = (typeof adConnectionStatuses)[number];

export const keywordMatchTypeSchema = z.enum(['EXACT', 'PHRASE', 'BROAD']);
const keywordSchema = z.object({ text: z.string().trim().min(1).max(80), matchType: keywordMatchTypeSchema });

/** Integer minor units and an ISO currency, bounded so it always fits the database column. */
export const moneySchema = z.object({
  amountMinor: z.number().int().min(1).max(2_000_000_000),
  currency: z.string().regex(/^[A-Z]{3}$/, 'Use a three-letter ISO currency code'),
});
export type MoneyInput = z.infer<typeof moneySchema>;

const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

/**
 * What an approved revision contains. Structure is validated here; provider limits (character counts, minimum counts)
 * are checked by the provider adapter at submit and again at publish, so a draft can be saved while it is incomplete.
 */
export const campaignSpecSchema = z.object({
  name: z.string().trim().min(1).max(255),
  objective: z.literal('LEADS'),
  dailyBudget: moneySchema,
  accountTimeZone: z.string().min(1).max(64),
  startDate: isoDate.nullable(),
  endDate: isoDate.nullable(),
  countries: z.array(z.string().regex(/^[A-Z]{2}$/)).max(50),
  languages: z.array(z.string().regex(/^[a-z]{2,3}(-[A-Za-z]{2,4})?$/)).max(20),
  destinationUrl: z.string().url().max(2000),
  keywords: z.array(keywordSchema).max(200),
  negativeKeywords: z.array(keywordSchema).max(200),
  creative: z.object({ headlines: z.array(z.string().max(200)).max(15), descriptions: z.array(z.string().max(300)).max(4) }),
});
export type CampaignSpecInput = z.infer<typeof campaignSpecSchema>;

export const createCampaignSchema = z.object({
  accountId: z.string().min(1),
  /** The exact published landing version traffic is sent to. */
  landingVersionId: z.string().min(1),
  spec: campaignSpecSchema,
  changeSummary: z.string().trim().max(280).optional(),
});
export type CreateCampaign = z.infer<typeof createCampaignSchema>;

export const reviseCampaignSchema = z.object({
  expectedVersion: z.number().int().min(0),
  landingVersionId: z.string().min(1).optional(),
  spec: campaignSpecSchema,
  changeSummary: z.string().trim().max(280).optional(),
});
export type ReviseCampaign = z.infer<typeof reviseCampaignSchema>;

export const campaignActionSchema = z.object({ expectedVersion: z.number().int().min(0) });
export type CampaignAction = z.infer<typeof campaignActionSchema>;

export const eligibilityInputSchema = z.object({
  countries: z.array(z.string().regex(/^[A-Z]{2}$/)).max(50),
  advertiser: z.boolean(),
  product: z.boolean(),
  account: z.boolean(),
});
export type EligibilityInput = z.infer<typeof eligibilityInputSchema>;

/** `revisionHash` is what the reviewer saw: approval fails if the campaign changed since. */
export const approveCampaignSchema = z.object({
  expectedVersion: z.number().int().min(0),
  revisionHash: z.string().regex(/^[0-9a-f]{64}$/),
  eligibility: eligibilityInputSchema.optional(),
  note: z.string().trim().max(500).optional(),
});
export type ApproveCampaign = z.infer<typeof approveCampaignSchema>;

export const requestChangesSchema = z.object({ expectedVersion: z.number().int().min(0), note: z.string().trim().min(1).max(500) });
export type RequestChanges = z.infer<typeof requestChangesSchema>;

export const publishCampaignSchema = z.object({
  expectedVersion: z.number().int().min(0),
  revisionHash: z.string().regex(/^[0-9a-f]{64}$/),
  /** Required to equal the daily budget when it is at or above the confirmation threshold. */
  confirmedDailyMinor: z.number().int().min(1).max(2_000_000_000).optional(),
});
export type PublishCampaign = z.infer<typeof publishCampaignSchema>;

export const budgetPolicySchema = z.object({
  currency: z.string().regex(/^[A-Z]{3}$/),
  maxDailyCampaignMinor: z.number().int().min(1).max(2_000_000_000),
  maxMonthlyAccountMinor: z.number().int().min(1).max(2_000_000_000),
  confirmationThresholdMinor: z.number().int().min(1).max(2_000_000_000),
}).refine((p) => p.confirmationThresholdMinor <= p.maxDailyCampaignMinor, { message: 'The confirmation threshold cannot exceed the daily campaign limit', path: ['confirmationThresholdMinor'] });
export type BudgetPolicyInput = z.infer<typeof budgetPolicySchema>;
export const budgetPolicySettingKey = 'ads.budgetPolicy';

export const adCampaignListQuerySchema = z.object({
  state: z.enum(adCampaignStates).optional(),
  accountId: z.string().optional(),
  landingId: z.string().optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(25),
});
export type AdCampaignListQuery = z.infer<typeof adCampaignListQuerySchema>;

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-09-30').refine((d) => !Number.isNaN(Date.parse(`${d}T00:00:00.000Z`)), 'Not a valid date');
/** Day window for performance reads. Defaults to the last 30 days ending today (UTC); at most 366 days. */
export const adOverviewQuerySchema = z.object({ from: isoDay.optional(), to: isoDay.optional(), accountId: z.string().optional() });
export type AdOverviewQuery = z.infer<typeof adOverviewQuerySchema>;
export const adPerformanceQuerySchema = z.object({ from: isoDay.optional(), to: isoDay.optional() });
export type AdPerformanceQuery = z.infer<typeof adPerformanceQuerySchema>;
export const adSyncRequestSchema = z.object({ connectionId: z.string().min(1) });
export type AdSyncRequest = z.infer<typeof adSyncRequestSchema>;

export const adDeliveryActions = ['PAUSE', 'RESUME'] as const;
export type AdDeliveryAction = (typeof adDeliveryActions)[number];
export const adDeliveryChangeStates = ['REQUESTED', 'APPLIED', 'FAILED'] as const;
export type AdDeliveryChangeState = (typeof adDeliveryChangeStates)[number];

// ---------- Responses ----------

export interface AdValidationIssue { field: string; code: string; message: string; remedy: string }

export interface AdProviderCapabilities {
  provider: AdProviderKey;
  isDryRun: boolean;
  liveAdapter: boolean;
  access: 'NONE' | 'READ_ONLY' | 'LIMITED_WRITE' | 'FULL_WRITE';
  createCampaign: boolean;
  updateBudget: boolean;
  pauseCampaign: boolean;
  performanceRead: boolean;
  offlineConversions: boolean;
  audienceCreation: boolean;
  createAdAccount: boolean;
  unavailableReason?: string;
}

export interface AdCreativeLimits {
  headline: { min: number; max: number; maxLength: number };
  description: { min: number; max: number; maxLength: number };
  keywordMaxLength: number;
  verified: boolean;
  source: string;
}

export interface AdProviderInfo { provider: AdProviderKey; label: string; status: AdConnectionStatus; capabilities: AdProviderCapabilities; limits: AdCreativeLimits }

export interface AdAccountSummary {
  id: string;
  name: string;
  provider: AdProviderKey;
  connectionStatus: AdConnectionStatus;
  currency: string;
  timeZone: string;
  externalId: string;
  isDemo: boolean;
  active: boolean;
  lastError: string | null;
  capabilities: AdProviderCapabilities;
  limits: AdCreativeLimits;
}

export interface AdBudgetPolicy extends BudgetPolicyInput { updatedAt: string | null }

export interface AdLandingOption {
  id: string;
  name: string;
  restrictedCategory: boolean;
  marketCode: string | null;
  versions: { id: string; versionNumber: number; localeCode: string; publicUrl: string; publishedAt: string }[];
}

export interface AdCampaignOptions {
  accounts: AdAccountSummary[];
  landings: AdLandingOption[];
  budgetPolicy: AdBudgetPolicy | null;
  dryRunAvailable: boolean;
}

export interface AdCampaignSummary {
  id: string;
  name: string;
  state: AdCampaignState;
  version: number;
  provider: AdProviderKey;
  isDemo: boolean;
  accountId: string;
  accountName: string;
  landingId: string;
  landingName: string;
  landingVersionNumber: number;
  marketCode: string | null;
  dailyBudgetMinor: number;
  currency: string;
  revisionNumber: number;
  revisionHash: string;
  approvedRevision: boolean;
  deliveryStatus: string | null;
  providerCampaignRef: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AdRevisionSummary {
  id: string;
  revisionNumber: number;
  contentHash: string;
  landingVersionId: string;
  landingVersionNumber: number;
  changeSummary: string | null;
  createdById: string;
  createdAt: string;
  approved: boolean;
  approvedAt: string | null;
}

export interface AdOperationSummary {
  id: string;
  revisionId: string;
  state: AdPublishOperationState;
  attempts: number;
  providerCampaignRef: string | null;
  lastErrorKind: string | null;
  lastSafeMessage: string | null;
  issues: AdValidationIssue[] | null;
  confirmedDailyMinor: number | null;
  createdAt: string;
  completedAt: string | null;
}

export interface AdCampaignDetail extends AdCampaignSummary {
  spec: CampaignSpecInput;
  revisions: AdRevisionSummary[];
  approval: { id: string; reviewerId: string; approvedAt: string; eligibility: EligibilityInput | null; note: string | null } | null;
  operations: AdOperationSummary[];
  /** Provider-side checks run now (capability, limits); empty means ready for review. */
  issues: AdValidationIssue[];
  budgetDecision: { decision: 'OK' | 'CONFIRM' | 'BLOCK'; reasons: string[] };
  /** Why publishing is not allowed right now; empty means the caller may publish. */
  publishBlockers: string[];
  restrictedCategory: boolean;
  limits: AdCreativeLimits;
  capabilities: AdProviderCapabilities;
  allowedActions: ('revise' | 'submit' | 'request-changes' | 'approve' | 'publish' | 'reconcile' | 'archive' | 'pause' | 'resume')[];
  /** Why pause/resume is not available right now (empty when it is, or when the campaign is not live). */
  deliveryBlockers: string[];
  /** Latest pause/resume request, including a failed one. The delivery status above changes only once the provider acknowledges. */
  deliveryChange: AdDeliveryChangeSummary | null;
  /** utm_campaign value and the exact URL the provider receives (the approved destination plus platform tracking parameters). */
  trackingKey: string;
  trackedDestinationUrl: string;
}

export interface AdDeliveryChangeSummary {
  id: string;
  action: AdDeliveryAction;
  state: AdDeliveryChangeState;
  attempts: number;
  resultStatus: string | null;
  lastErrorKind: string | null;
  lastSafeMessage: string | null;
  requestedById: string;
  createdAt: string;
  completedAt: string | null;
}

/** Counts are whole numbers; money is integer minor units in `currency`. Revenue stays per currency and is never converted. */
export interface AdMetrics {
  impressions: number;
  clicks: number;
  spendMinor: number;
  /** Leads whose first-party touchpoint carries the campaign's tracking key. A lower bound: no analytics consent means no touchpoint. */
  leads: number;
  /** Attributed leads currently qualified, in proposal or won. */
  qualifiedLeads: number;
  wonDeals: number;
  /** Won deal amounts as exact decimal strings, one entry per currency; null when the viewer may not see revenue. */
  revenue: { currency: string; amount: string }[] | null;
  /** Spend per qualified lead in minor units of the spend currency; null with no qualified lead. */
  cpqlMinor: number | null;
  /** Revenue ÷ spend with two decimals, only when both are in the same single currency and spend is above zero. */
  roas: string | null;
}

export interface AdCampaignPerformanceRow {
  id: string;
  name: string;
  state: AdCampaignState;
  /** Campaign version, sent back as `expectedVersion` with a pause/resume request. */
  version: number;
  deliveryStatus: string | null;
  deliveryChange: AdDeliveryChangeSummary | null;
  provider: AdProviderKey;
  isDemo: boolean;
  accountId: string;
  accountName: string;
  currency: string;
  dailyBudgetMinor: number;
  landingName: string;
  marketCode: string | null;
  metrics: AdMetrics;
  /** Last day with synced numbers, and when that sync ran. Null when this campaign has never been synced. */
  dataThrough: string | null;
  syncedAt: string | null;
}

export interface AdSeriesPoint { date: string; spendMinor: number; clicks: number; leads: number }

export interface AdConnectionFreshness {
  id: string;
  name: string;
  provider: AdProviderKey;
  status: AdConnectionStatus;
  isDemo: boolean;
  performanceRead: boolean;
  lastSyncAt: string | null;
  lastSyncAttemptAt: string | null;
  lastError: string | null;
}

export interface AdOverview {
  range: { from: string; to: string };
  /** Lead and revenue days are bucketed in UTC; provider spend days are in each ad account's own time zone. */
  reportingTimeZone: 'UTC';
  /** One entry per currency; currencies are never added together. */
  totals: { currency: string; isDemo: boolean; campaigns: number; metrics: AdMetrics }[];
  series: { currency: string; points: AdSeriesPoint[] }[];
  campaigns: AdCampaignPerformanceRow[];
  connections: AdConnectionFreshness[];
  hasDemoData: boolean;
  revenueVisible: boolean;
  /** Plain-language limits: reporting delay, consent-aware attribution. */
  notes: string[];
}

export interface AdCampaignPerformance {
  campaignId: string;
  range: { from: string; to: string };
  currency: string;
  isDemo: boolean;
  metrics: AdMetrics;
  daily: { date: string; impressions: number; clicks: number; spendMinor: number | null; leads: number; qualifiedLeads: number }[];
  dataThrough: string | null;
  syncedAt: string | null;
  connection: AdConnectionFreshness;
  notes: string[];
}

export interface AdCampaignList { items: AdCampaignSummary[]; nextCursor: string | null; counts: Record<string, number> }

export interface AdCommitted {
  /** Daily budget × 30 of the account's other approved or live campaigns: a planning figure, not provider-billed spend. */
  committedExposureMinor: number;
}
